import { Component, type ReactNode } from "react";
import { persister } from "../lib/persist";

const RETRIED = "pw-render-retry";

/** Bewaarde gegevens weggooien en opnieuw laden: een oude of kapotte cache blijft anders na herladen hangen. */
async function freshReload() {
  try {
    await persister.removeClient();
  } catch {
    /* geen opslag */
  }
  window.location.reload();
}

/**
 * Vangt een fout tijdens het tekenen van een pagina op. Zonder deze grens toont React een leeg
 * scherm. De eerste keer gooit de app de bewaarde gegevens weg en laadt opnieuw (een bewaarde
 * cache wordt anders na herladen gewoon weer getoond); gaat het daarna nog mis, dan ziet de
 * gebruiker wat er misging. Opdrachten die op verbinding wachten blijven bewaard.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch() {
    try {
      if (sessionStorage.getItem(RETRIED)) return;
      sessionStorage.setItem(RETRIED, "1");
    } catch {
      return;
    }
    void freshReload();
  }

  override componentDidMount() {
    setTimeout(() => {
      try {
        if (!this.state.error) sessionStorage.removeItem(RETRIED);
      } catch {
        /* geen opslag */
      }
    }, 10_000);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="center stack" role="alert">
        <h1>Er ging iets mis</h1>
        <p>
          Deze pagina kon niet worden getoond. Laad de pagina opnieuw; lukt het dan nog niet, stuur ons deze melding.
        </p>
        <p className="muted small">
          <code>{error.message}</code>
        </p>
        <p>
          <button type="button" className="btn primary" onClick={() => void freshReload()}>
            Opnieuw laden
          </button>
        </p>
      </div>
    );
  }
}
